import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePickerComponent from '../../common/DatePickerComponent';
import Select from 'react-select';
import _ from 'lodash';
import { getSourceListList } from '../../../store/actions/incoming';
import { fetchCompanyFilters, getFilterSellers } from '../../../store/actions/rateCard';
import { getAllSellersList } from '../../../store/actions';
import { computeAutoSelectedSellers } from '../../../config/utils';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import {
  buildCompanyOptions,
  buildSellerOptions,
  getCountsToUse,
  syncSelected,
} from './packageAssignmentFilterUtils';

const PackageAssignmentFilter = ({
  filterData,
  setFilterData,
  getPackageAssignmentList,
  handleReset
}) => {
  const [companyList, setCompanyList] = useState([]);
  const [sellerList, setSellerList] = useState([]);
  const [allSellers, setAllSellers] = useState([]);
  const [sourcesList, setSourcesList] = useState([]);
  const sourcesListData = useSelector(
      (state) => state.IncomingReducer.sourcesList
    );
  const { filterCompanies, filterAllSellers } = useSelector((state) => state.RateCardReducer);
  const dispatch = useDispatch();

  useEffect(() => {
    if (sourcesListData.length === 0) {
      dispatch(getSourceListList());
    } else {
      let sourcesListCopyObj = JSON.parse(JSON.stringify(sourcesListData));
      setSourcesList(getSelectList(sourcesListCopyObj, 'id', 'source'));
    }
  }, [sourcesListData]);

  useEffect(() => {
    let data = {
        pageIndex: 1,
        pageSize: 10,
        sortColumn: 'name',
        sortOrder: 'ascending'
    };
    let sellerData = {
        "pageIndex": 1,
        "pageSize": 10,
        "sortColumn": "name",
        "sortOrder": "ASCENDING",
        "companyIdList": [0],
        screenType: 2,
    };
    dispatch(fetchCompanyFilters(data));
    dispatch(getFilterSellers(sellerData));
  }, []);

  useEffect(() => {
    let companyList = filterCompanies?.Entity?.listCompanyMasterOrdersEntity || [];
    let sellerList = filterAllSellers?.EntityList || [];
    if (companyList.length) {
        setCompanyList(getSelectList(filterCompanies?.Entity?.listCompanyMasterOrdersEntity, 'Id', 'name'));
    }
    if (sellerList.length) {
        setAllSellers(getSelectList(sellerList, 'id', 'sellerName'));
        setSellerList(getSelectList(sellerList, 'id', 'sellerName'));
    }
  }, [filterCompanies, filterAllSellers]);

  // Counts returned by the list API for the last applied filters (null => not available)
  const companySellerCounts = useSelector(
    (state) => state.packageAssignmentReducer?.companySellerCounts
  );
  const isCountsDateFiltered = useSelector(
    (state) => state.packageAssignmentReducer?.isCompanySellerCountsDateFiltered
  );
  const hasDateFilter = !!(filterData.fromDate && filterData.toDate);
  const countsToUse = getCountsToUse(companySellerCounts, isCountsDateFiltered, hasDateFilter);

  // With date filters: only companies/sellers present in the API counts, labelled with their counts
  const companyOptions = useMemo(
    () => (countsToUse ? buildCompanyOptions(companyList, countsToUse) : companyList),
    [companyList, countsToUse]
  );

  const sellerOptions = useMemo(
    () =>
      countsToUse
        ? buildSellerOptions([...sellerList, ...allSellers], countsToUse, filterData.companyIdList)
        : sellerList,
    [sellerList, allSellers, countsToUse, filterData.companyIdList]
  );

  // Generic (no date filter) dropdowns keep the stored selection untouched
  const getSelectedValue = (selected, options) =>
    countsToUse ? syncSelected(selected, options) : selected;

  const fetchSellers = async (companyList) => {
    if (companyList.length === 0) {
        setSellerList(allSellers);
        return;
    }
    let data = {
        companyIds: companyList,
    };
    try {
        let res = await getAllSellersList(data);
        if (res?.data?.Status === 200) {
            let dataList = res?.data?.Entity?.listSellerMasterOrdersEntity.map(
                (item) => {
                    return {
                        ...item,
                        label: item.sellerAndCompany,
                        value: item.id,
                    };
                }
            );
            setSellerList(dataList);
            // Auto-select sellers for any selected company that has exactly one seller
            setFilterData((prev) => {
                const merged = computeAutoSelectedSellers(dataList, companyList, prev?.sellerIdList || []);
                if (merged.length === (prev?.sellerIdList?.length || 0)) return prev;
                return { ...prev, sellerIdList: merged };
            });
        }
    } catch (err) {
      console.error('Error fetching sellers:', err);
    }
  };

  const handleMultiSelect = (value, fieldName) => {
    if (fieldName === "companyIdList") {
      const companyIds = _.map(value, "Id");
      fetchSellers(companyIds);
      
      let newFilterData = {
        ...filterData,
        [fieldName]: value
      };
      
      if (filterData?.sellerIdList?.length) {
        const filteredSellers = filterData.sellerIdList.filter(seller =>
          companyIds.includes(Number.parseInt(seller.companyId))
        );
        newFilterData.sellerIdList = filteredSellers;
      }
      
      setFilterData(newFilterData);
    } else {
      setFilterData({
        ...filterData,
        [fieldName]: value
      });
    }
  };

  const handleSingleSelect = (value, fieldName) => {
    setFilterData({
      ...filterData,
      [fieldName]: value || ""
    });
  };

  const handleDateFieldChange = (date, field) => {
    if (field === "fromDate" && filterData.toDate && date > filterData.toDate) {
      toast.error("From Date cannot be later than To Date");
      return;
    }
    setFilterData({ ...filterData, [field]: date });
  };

  const handleApplyFilter = () => {
    const data = { ...filterData };
    data.pageIndex = 1;
    data.sortColumn = '';
    data.sortOrder = 'DESCENDING';
    getPackageAssignmentList(data);
  };

  const getSelectList = (list, valueField, labelField) => {
    return list?.map((item) => ({
      ...item,
      value: item[valueField],
      label: item[labelField]
    })) || [];
  };

  return (
    <div className="filter_box_main">
      <div className="filter_box">
        <div className="row">

          {/* Status Filter */}
          <div className='col-lg-2 col-md-4 col-sm-6 col-12'>
            <div className='row'>
              <div className='col-12'>
                <div className='labelHead'>{" "}
                  <i className="fa fa-flag mr-2"/> {" "}
                  Source
                </div>
              </div>
              <div className='col-12 fontFix'>
                <Select
                  closeMenuOnSelect={false}
                  value={filterData.sources}
                  onChange={(value) => handleSingleSelect(value, 'sources')}
                  options={sourcesList}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select Status"
                  isClearable
                  isMulti
                />
              </div>
            </div>
          </div>

          {/* Company Filter */}
          <div className='col-lg-2 col-md-4 col-sm-6 col-12'>
            <div className='row'>
              <div className='col-12'>
                <div className='labelHead'>
                  <i className="fa fa-building mr-2"></i> {" "}
                  Company
                </div>
              </div>
              <div className='col-12 fontFix'>
                <Select
                  value={getSelectedValue(filterData.companyIdList, companyOptions)}
                  onChange={(value) => handleMultiSelect(value, 'companyIdList')}
                  options={companyOptions}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select Company"
                  isMulti
                  isClearable
                />
              </div>
            </div>
          </div>

          {/* Seller Filter */}
          <div className='col-lg-2 col-md-4 col-sm-6 col-12'>
            <div className='row'>
              <div className='col-12'>
                <div className='labelHead'>
                  <i className="fa fa-user mr-2"></i> {" "}
                  Seller
                </div>
              </div>
              <div className='col-12 fontFix'>
                <Select
                  value={getSelectedValue(filterData.sellerIdList, sellerOptions)}
                  onChange={(value) => handleMultiSelect(value, 'sellerIdList')}
                  options={sellerOptions}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select Seller"
                  isMulti
                  isClearable
                />
              </div>
            </div>
          </div>

          {/* From Date */}
          <div className='col-lg-2 col-md-4 col-sm-6 col-12'>
            <div className='row'>
              <div className='col-12'>
                <div className='labelHead' >
                  <i className="fa fa-calendar mr-2"></i> {" "}
                  From Date
                </div>
              </div>
              <div className='col-12 fontFix'>
                <DatePickerComponent
                  dateField={filterData.fromDate}
                  handleDateChange={(date) => handleDateFieldChange(date, 'fromDate')}
                  fieldName="fromDate"
                  dateFormat="MM-dd-yyyy"
                />
              </div>
            </div>
          </div>

          {/* To Date */}
          <div className='col-lg-2 col-md-4 col-sm-6 col-12'>
            <div className='row'>
              <div className='col-12'>
                <div className='labelHead'> {" "}
                  <i className="fa fa-calendar mr-2"></i> {" "}
                  To Date
                </div>
              </div>
              <div className='col-12 fontFix'>
                <DatePickerComponent
                  dateField={filterData.toDate}
                  handleDateChange={(date) => handleDateFieldChange(date, 'toDate')}
                  fieldName="toDate"
                  dateFormat="MM-dd-yyyy"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className='col-2 mt-3'>
            <div className='align-items-center'>
              <div className='d-flex gap-3 align-items-center pt-2'>
                <button
                  className="btn cus-primary-transparent-btn clrBtn mr-2"
                  onClick={handleReset}
                >
                  Clear
                </button>
                <button
                  className="btn cus-seconday-bg-btn applyBtn"
                  onClick={handleApplyFilter}
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PackageAssignmentFilter.propTypes = {
  filterData: PropTypes.object.isRequired,
  setFilterData: PropTypes.func.isRequired,
  getPackageAssignmentList: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
};

export default PackageAssignmentFilter;